import React, { useCallback, useMemo } from 'react';

import { Form, Input, Select, Switch, Space } from 'antd';
import { Formik } from 'formik';

import { ILPAccountUserState } from '../../hooks/useLPAccountUser';
import { PreferredLanguage } from '../../models/lpaccount';
import { updateProfileSchema } from '../../schemas/lpaccountSchema';
import EditButtonsOnForm from '@/components/EditButtonsOnForm';
import FormRow from '@/components/forms/FormRow';
import { scopes } from '@/config';

interface Props {
  user: ILPAccountUserState;
  saveUserData: (data) => Promise<{ success: boolean }>;
  isSSO: boolean;
}

const { Item } = Form;

const LANG_OPTIONS: { value: PreferredLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
];

const LPAccountForm = ({ user, saveUserData, isSSO }: Props) => {
  const initialValues = useMemo(() => user.data, [user]);
  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true);
      await saveUserData(values);
      setSubmitting(false);
    },
    [saveUserData]
  );

  const createHandleChange = useCallback(
    (handleChange) => (name: string) => (value: any) => {
      handleChange({ target: { name, value } });
    },
    []
  );

  if (user.loading) {
    return <></>;
  }

  return (
    <>
      <Formik<UpdateUserProfileFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={updateProfileSchema}
      >
        {({
          handleChange,
          handleSubmit,
          handleReset,
          handleBlur,
          values,
          isSubmitting,
          dirty,
          errors,
        }) => {
          const handleFieldChange = createHandleChange(handleChange);

          return (
            <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
              <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
              >
                <FormRow>
                  <Item label="First Name" style={{ flex: 1, marginBottom: 0 }}>
                    <Input
                      id="firstName"
                      name="firstName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.firstName}
                    />
                  </Item>
                  <Item label="Last Name" style={{ flex: 1, marginBottom: 0 }}>
                    <Input
                      id="lastName"
                      name="lastName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastName}
                    />
                  </Item>
                  <Item label="Given Name" style={{ flex: 1, marginBottom: 0 }}>
                    <Input
                      id="givenName"
                      name="givenName"
                      value={values.givenName}
                      disabled
                    />
                  </Item>
                </FormRow>
                <FormRow>
                  <Item
                    label="Phone Number"
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phoneNumber}
                    />
                  </Item>
                  <Item
                    label="Email Verified"
                    style={{
                      flex: 1,
                      marginBottom: 0,
                      ...(isSSO ? { pointerEvents: 'none', opacity: 0.5 } : {}),
                    }}
                  >
                    <Switch
                      id="emailVerified"
                      onChange={handleFieldChange('emailVerified')}
                      checked={values.emailVerified}
                    />
                  </Item>
                  <Item
                    label="Account Verified"
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Switch
                      id="accountVerified"
                      onChange={handleFieldChange('accountVerified')}
                      checked={values.accountVerified}
                    />
                  </Item>
                </FormRow>
                <FormRow width="100%">
                  <Item
                    label="Preferred Language"
                    required
                    validateStatus={
                      errors.preferredLanguage ? 'error' : undefined
                    }
                    help={errors.preferredLanguage}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Select
                      id="preferredLanguage"
                      placeholder="Select language"
                      value={values.preferredLanguage as string}
                      onChange={handleFieldChange('preferredLanguage')}
                      onBlur={handleBlur}
                      status={errors.preferredLanguage ? 'error' : undefined}
                      style={{ width: '100%' }}
                      options={LANG_OPTIONS}
                    />
                  </Item>
                  <Item
                    label="Public Handle"
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input
                      id="publicHandle"
                      name="publicHandle"
                      value={values.publicHandle}
                      disabled
                    />
                  </Item>
                </FormRow>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <EditButtonsOnForm
                    handleCancel={handleReset}
                    handleSaveEdit={handleSubmit}
                    disabledCancel={isSubmitting || !dirty}
                    disabledSubmit={isSubmitting || !dirty}
                    requiredScopes={[scopes.EDIT_LPACCOUNT]}
                  />
                </Space>
              </Space>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

interface UpdateUserProfileFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailVerified: boolean;
  accountVerified: boolean;
  preferredLanguage: PreferredLanguage;
  publicHandle: string;
  givenName: string;
}

export default LPAccountForm;
